import React, { createContext, useContext, useMemo, useRef } from 'react';

/**
 * What is a "rendering tracker"?
 * ------------------------------
 * Clinical impression markdown often cites the same underlying thing more
 * than once — the same lab, the same biomarker code, the same PDF page. Each
 * citation is rendered by its own `<InlineAttachment>`, so without
 * coordination the user sees the same chart or PDF panel multiple times in
 * one report. That's the "duplicate attachments" problem on Annual Reports
 * and Zori Reports.
 *
 * A rendering tracker is the small piece of shared state that lets sibling
 * widgets agree on "I've already shown this — you can skip it." The first
 * widget to ask about a given key is told to render; later widgets asking
 * about the same key are told to render nothing.
 *
 * `createRenderingTracker` is the generic factory for this pattern. Each
 * attachment type (Observation, DocumentReference, ...) builds its own
 * tracker by passing a key function that decides what counts as "the same
 * thing" for that type. The trackers themselves live in `./trackers/`.
 */

/**
 * Branded string used as the dedup identity inside a tracker session.
 */
export type TrackingKey = string & { readonly __brand: 'TrackingKey' };

/**
 * Constructs a `TrackingKey` from a plain string. Use inside a tracker's
 * `getKey` implementation when assembling the dedup identity (e.g.
 * ``trackingKey(`${code}|${system}|${summaryType}`)``).
 */
export const trackingKey = (value: string): TrackingKey => value as TrackingKey;

/**
 * How a tracker decides "are these two inputs the same widget?".
 *
 * Return a `TrackingKey` the tracker should treat as the dedup identity
 * (e.g. the resource id, or `code|system|impressionType`). Return `null`
 * if the input has no stable identity yet (e.g. an attachment with no
 * primary code) — in that case `shouldRender` will return `false` because
 * there is nothing safe to dedupe against.
 */
export type GetTrackingKey<TInput> = (input: TInput) => TrackingKey | null;

/**
 * The minimal API a consumer needs at a render call site: "given this
 * attachment, should I render its widget?". `true` means "you are the first,
 * go ahead"; `false` means "a sibling already rendered this, skip yourself".
 */
export type RenderingTracker<TInput> = {
  shouldRender: (input: TInput) => boolean;
};

export type RenderingTrackerHandle<TInput> = {
  Provider: React.FC<{ context: string; children: React.ReactNode }>;
  useTracker: () => RenderingTracker<TInput>;
};

type Session = { seen: Set<TrackingKey> };

type ContextValue = {
  session: Session;
  claim: (key: TrackingKey) => boolean;
};

const newSession = (): Session => ({ seen: new Set() });

const defaultContextValue: ContextValue = {
  session: newSession(),
  claim: () => true,
};

/**
 * Builds a `Provider` + `useTracker` pair for one attachment type.
 *
 * What it does
 * - The `Provider` defines a coordination scope (one report, one print
 *   view, ...). Inside that scope the first widget asking about a given key
 *   wins; later widgets with the same key are told to skip rendering.
 * - The `useTracker` hook is what each widget calls to ask "should I
 *   render?". Each consumer remembers its own answer for the lifetime of
 *   the session so re-renders stay stable — a widget that rendered once
 *   does not flicker out when its parent re-renders.
 *
 * Why a `context` prop on the Provider
 * - Some events (entering edit mode, switching impression filter, opening a
 *   new report) really should reset who-rendered-what so the user sees a
 *   fresh layout. Bumping the `context` string is the explicit signal for
 *   "this is a new viewing session"; the tracker drops everything it had
 *   memorized and starts over.
 *
 * Why "no Provider" still works
 * - Some screens render `<InlineAttachment>` outside an Annual Report
 *   surface (e.g. one-off previews). With no Provider mounted, dedup is
 *   irrelevant and `shouldRender` returns `true` so those screens keep
 *   working unchanged.
 */
export function createRenderingTracker<TInput>(
  getKey: GetTrackingKey<TInput>,
): RenderingTrackerHandle<TInput> {
  const Ctx = createContext<ContextValue>(defaultContextValue);

  const Provider: RenderingTrackerHandle<TInput>['Provider'] = ({
    context,
    children,
  }) => {
    const sessionRef = useRef<Session>(newSession());
    const prevContextRef = useRef(context);

    // When the context string changes (e.g. a new impression is selected, or
    // the user enters/exits edit mode), replace the session so the tracker
    // starts fresh. The replacement happens synchronously during render,
    // before useMemo runs, so the new object reference is what useMemo sees.
    if (prevContextRef.current !== context) {
      sessionRef.current = newSession();
      prevContextRef.current = context;
    }

    const value = useMemo<ContextValue>(
      () => ({
        session: sessionRef.current,
        claim: key => {
          const seen = sessionRef.current.seen;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        },
      }),
      // sessionRef.current is *replaced* (not mutated) before this runs, so
      // the new object reference correctly invalidates the memo on context
      // change without causing an extra render cycle.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [sessionRef.current],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  };

  const useTracker = (): RenderingTracker<TInput> => {
    const { session, claim } = useContext(Ctx);

    const cacheRef = useRef<{
      session: Session;
      map: Map<TrackingKey, boolean>;
    }>({
      session,
      map: new Map(),
    });

    if (cacheRef.current.session !== session) {
      cacheRef.current = { session, map: new Map() };
    }

    return useMemo(
      () => ({
        shouldRender: (input: TInput) => {
          const key = getKey(input);
          if (key == null) return false;
          const cached = cacheRef.current.map.get(key);
          if (cached !== undefined) return cached;
          const allowed = claim(key);
          cacheRef.current.map.set(key, allowed);
          return allowed;
        },
      }),
      // `session` is omitted: `claim` is recreated whenever session changes
      // (it comes from the Provider's useMemo which depends on session), so
      // `claim` alone is sufficient to invalidate this memo.
      [claim],
    );
  };

  return { Provider, useTracker };
}
